/**
 * A small in-memory `GameApi`: plausible data, simple rules, and a log of every call so a test
 * can check the wiring. It backs the `?ui=<name>` fixture routes and the menu tests; the real
 * one is src/meta's `createGameApi`. The roster methods are in fakeRosters.ts, the round-two
 * methods (intel, stock, training, the daily, records) in fakeExtras.ts.
 */

import type { Deployment, GameApi, PriceList } from '../../../meta/api';
import type { Aftermath, ArmoryItem, MissionSummary, Operation, Profile, RangerRecord } from '../../../meta/types';
import type { Loadout, MissionParams } from '../../../sim/types';
import { operation, profile as makeProfile, ranger, recruits } from './data';
import type { FakeExtras } from './fakeExtras';
import { createExtraMethods, emptyExtras, showcaseExtras, takeGear } from './fakeExtras';
import { createRosterMethods } from './fakeRosters';

/** Specialist gear is found, never bought (04 §6): it has a weight and a stock, but no price. */
const PRICE: Record<ArmoryItem, number> = {
  magazines: 1,
  roundsInMag: 0,
  grenades: 2,
  laws: 6,
  bombs: 4,
  firstAid: 3,
  smoke: 0,
  nvg: 0,
  'wire-cutters': 0,
  'suppressed-pistol': 0,
  ghillie: 0,
};

const WEIGHT: Record<ArmoryItem, number> = {
  magazines: 1,
  roundsInMag: 0,
  grenades: 2,
  laws: 6,
  bombs: 4,
  firstAid: 2,
  smoke: 1,
  nvg: 1,
  'wire-cutters': 1,
  'suppressed-pistol': 2,
  ghillie: 2,
};

const ISSUE: Record<string, number> = { magazines: 4, grenades: 3, laws: 1, bombs: 1, firstAid: 1 };

const NAMES: Readonly<Record<string, string>> = {
  magazines: 'Magazine',
  grenades: 'Grenade',
  laws: 'LAW rocket',
  bombs: 'Time bomb',
  firstAid: 'First-aid kit',
  smoke: 'Smoke grenade',
  nvg: 'Night-vision goggles',
  ghillie: 'Ghillie wrap',
  'sam-site': 'Disable a SAM Site',
  pipeline: 'Cut a Pipeline',
};

function title(id: string): string {
  return id.split(/[-:.]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export interface FakeOptions {
  withOperation?: boolean;
  /**
   * Everything round two added, switched on: a Tag Recovery node, a perk to train, specialist
   * stock, recovered tags on the wall, a flown daily table and some records.
   */
  showcase?: boolean;
}

export interface FakeApi extends GameApi {
  /** Method names in call order, e.g. `deploy`, `visitSupportNode`. */
  readonly calls: string[];
  /** Make the next `deploy` / `deployClassic` throw this message. */
  failNextDeploy: string | null;
  state(): Profile;
  /** What the fake holds behind `stock()`, `daily()` and `records()`. */
  extras(): FakeExtras;
}

function showcase(data: Profile): void {
  const op = data.operation;
  if (op !== null) {
    op.trainablePerks = ['knife-work'];
    const node = op.nodes.find((n) => n.id === 'n0b');
    if (node !== undefined) node.tagRecovery = { name: 'CPL JOSS HALE' };
  }
  // The man whose tags the node above brings home. Found by his FATE, not his place in the list:
  // tags come off a body, so a POW who was never recovered can never have his confirmed - and the
  // wall is newest first, so an index here would quietly follow whoever died last.
  const recovered = data.memorial.find((e) => e.fate === 'kia');
  if (recovered !== undefined) recovered.tagsRecovered = true;
}

export function createFakeApi(options: FakeOptions = {}): FakeApi {
  const data = makeProfile(options.withOperation ?? true);
  const extras = options.showcase === true ? showcaseExtras() : emptyExtras();
  if (options.showcase === true) showcase(data);
  const calls: string[] = [];
  let nextId = 100;

  const op = (): Operation => {
    if (data.operation === null) throw new Error('NO OPERATION IS ACTIVE');
    return data.operation;
  };
  const find = (id: string): RangerRecord => {
    const pools = [data.operation?.company ?? [], data.classicRoster, data.hall];
    for (const pool of pools) {
      const hit = pool.find((r) => r.id === id);
      if (hit !== undefined) return hit;
    }
    throw new Error(`NO SUCH RANGER: ${id}`);
  };
  const loadoutCost = (loadout: Loadout): number => {
    let cost = 0;
    for (const [item, issue] of Object.entries(ISSUE)) {
      const key = item as keyof Loadout['carried'];
      const extra = Math.max(0, loadout.carried[key] - issue);
      const podded = loadout.pods.reduce((sum, pod) => sum + pod[key], 0);
      cost += (extra + podded) * PRICE[key];
    }
    return cost;
  };
  const guardDeploy = (): void => {
    if (api.failNextDeploy !== null) {
      const message = api.failNextDeploy;
      api.failNextDeploy = null;
      throw new Error(message);
    }
  };

  const api: FakeApi = {
    calls,
    failNextDeploy: null,
    state: () => data,
    extras: () => extras,
    ...createExtraMethods({ data, extras, calls, op, find }),
    ...createRosterMethods({ data, calls, find, nextId: () => nextId++ }),

    profile() {
      calls.push('profile');
      return data;
    },
    startOperation(difficulty, seed, cadet, veteranIds) {
      calls.push('startOperation');
      const vets = data.hall.filter((r) => veteranIds.includes(r.id)).map((r) => ({ ...r, veteran: true }));
      data.hall = data.hall.filter((r) => !veteranIds.includes(r.id));
      data.operation = operation({ difficulty, seed, cadet, company: [...recruits(), ...vets] });
      return data.operation;
    },
    withdrawOperation() {
      calls.push('withdrawOperation');
      op().status = 'withdrawn';
      data.operation = null;
    },
    openNodes() {
      calls.push('openNodes');
      return data.operation === null ? [] : data.operation.nodes.filter((n) => n.state === 'open');
    },
    visitSupportNode(nodeId, choice) {
      calls.push(`visitSupportNode:${nodeId}:${choice}`);
      calls.push('visitSupportNode');
      const o = op();
      if (nodeId === 'hq:recruit') {
        if (o.rp < 12) throw new Error('A replacement costs 12 RP.');
        o.rp -= 12;
        o.company.push(ranger(`q${nextId++}`, { firstName: 'New', surname: 'Hand', callsign: 'Rook' }));
        return 'New Hand reports for duty (-12 RP).';
      }
      if (nodeId.startsWith('hq:medevac:')) {
        const r = find(nodeId.slice('hq:medevac:'.length));
        r.recoveryMissions = Math.max(0, r.recoveryMissions - 1);
        if (r.recoveryMissions === 0) r.status = 'ready';
        o.rp -= 10;
        return 'Priority Medevac done (-10 RP).';
      }
      const node = o.nodes.find((n) => n.id === nodeId);
      if (node === undefined || node.kind === 'mission' || node.kind === 'rescue') {
        throw new Error('NOT A SUPPORT NODE');
      }
      for (const n of o.nodes) if (n.state === 'open') n.state = n.id === nodeId ? 'done' : 'skipped';
      for (const id of node.links) {
        const next = o.nodes.find((n) => n.id === id);
        if (next !== undefined) next.state = 'open';
      }
      o.position = nodeId;
      if (node.kind === 'supply' && choice === 0) o.rp += 15;
      const line = `${title(node.kind).toUpperCase()}: OPTION ${choice + 1} TAKEN.`;
      o.log.push(line);
      return line;
    },
    prices(rangerId): PriceList {
      calls.push('prices');
      const bonus = find(rangerId).traits.includes('farm-strong') ? 3 : 0;
      return { price: PRICE, weight: WEIGHT, carryCap: 30 + bonus, podCap: 21, maxPods: 3 };
    },
    deploy(nodeId, rangerId, loadout): Deployment {
      calls.push('deploy');
      guardDeploy();
      const o = op();
      const node = o.nodes.find((n) => n.id === nodeId);
      if (node === undefined || node.params === null) throw new Error('THAT NODE IS NOT A MISSION');
      const cost = loadoutCost(loadout);
      if (cost > o.rp) throw new Error(`NOT ENOUGH REQUISITION: ${cost} RP NEEDED, ${o.rp} HELD`);
      takeGear(extras, loadout.gear);
      o.rp -= cost;
      o.position = nodeId;
      return { params: node.params, rangerId, loadout, mods: {}, mode: 'operation' };
    },
    deployClassic(params: MissionParams, rangerId, loadout): Deployment {
      calls.push('deployClassic');
      guardDeploy();
      find(rangerId);
      return { params, rangerId, loadout, mods: {}, mode: 'classic' };
    },
    practice(params): Deployment {
      calls.push('practice');
      const carried = { magazines: 4, roundsInMag: 30, grenades: 3, laws: 1, bombs: 1, firstAid: 1 };
      return { params, rangerId: 'practice', loadout: { carried, pods: [] }, mods: {}, mode: 'practice' };
    },
    report(deployment, summary: MissionSummary): Aftermath {
      calls.push('report');
      // The Ranger of the Day is on no roster: the daily only fills in its line of the table.
      const daily = deployment.mode === 'daily';
      const id = deployment.rangerId;
      const r = daily ? ranger(id, { firstName: 'Ranger', surname: 'Hollis' }) : find(id);
      if (daily) {
        const entry = extras.daily.find((e) => `daily:${e.date}` === id);
        if (entry !== undefined) {
          entry.outcome = summary.outcome;
          entry.score = summary.score;
          entry.secondsUsed = summary.secondsUsed ?? 0;
        }
      } else {
        r.merit += summary.score;
      }
      return {
        ranger: r,
        meritGained: daily ? 0 : summary.score,
        rpGained: daily ? 0 : 12,
        promotedTo: null,
        perkChoices: null,
        medals: [],
        readinessChange: daily ? 0 : 1,
        readinessReasons: daily ? [] : ['MISSION ATTEMPTED'],
        freedRangers: [],
        memorial: null,
        operationStatus: 'active',
      };
    },
    describe(id) {
      // Like the real API: a node id describes what that node offers, `hq:` services are
      // described, and an id nobody knows (`choice:...`) describes itself.
      const node = data.operation?.nodes.find((n) => n.id === id);
      if (node !== undefined) return { name: title(node.kind), text: `What ${node.id} offers: (0) this, (1) that.` };
      if (id === 'hq:recruit') return { name: 'Replacement', text: 'A recruit from HQ for 12 RP.' };
      if (id === 'hq:medevac') return { name: 'Priority Medevac', text: 'One recovery step now, for 10 RP.' };
      // `category:demolition`, `group:1987`, `tag:no-drop`: named by what follows the prefix, as the real table does.
      const facet = /^(?:category|group|tag):(.+)$/.exec(id);
      if (facet !== null) return { name: title(facet[1] ?? id), text: `Notes on ${id}.` };
      if (id.includes(':')) return { name: id, text: '' };
      return { name: NAMES[id] ?? title(id), text: `Notes on ${id}.` };
    },
  };
  return api;
}
